import { defaultBusiness as NAP } from "@/lib/site-config";
import { useState, useEffect } from "react";
import { Phone, MessageCircle, ArrowLeft, ArrowRight, Check, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent, getWhatsAppUrl } from "@/lib/tracking";
import { submitLead } from "@/lib/leads.functions";
import { buildLeadAttribution } from "@/lib/lead-attribution";
import { useVerification } from "@/hooks/useVerification";

const serviceChips = [
  { value: "mot", label: "MOT Testing" },
  { value: "interim-service", label: "Interim Service" },
  { value: "full-service", label: "Full Service" },
  { value: "major-service", label: "Major Service" },
  { value: "diagnostics", label: "Diagnostics / Warning Light" },
  { value: "brakes", label: "Brake Repair" },
  { value: "clutch-gearbox", label: "Clutch / Gearbox" },
  { value: "bodywork", label: "Bodywork / Paint" },
  { value: "other", label: "Other" },
];

const timeSlots = ["Morning (9–12)", "Afternoon (12–6)", "Flexible"];
const urgencyOptions = ["Today", "This Week", "No Rush"];

const STORAGE_KEY = "woodseer_booking_flow_v1";
const VALID_SERVICES = new Set(serviceChips.map((s) => s.value));

interface PersistedBookingState {
  step: number;
  selectedService: string;
  otherDesc: string;
  reg: string;
  makeModel: string;
  year: string;
  notes: string;
  name: string;
  phone: string;
  email: string;
  prefDate: string;
  prefTime: string;
  urgency: string;
  area?: string;
}

function readPersisted(): Partial<PersistedBookingState> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedBookingState>;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      ...parsed,
      step: Math.min(3, Math.max(1, Number(parsed.step) || 1)),
      selectedService: parsed.selectedService && VALID_SERVICES.has(parsed.selectedService) ? parsed.selectedService : "",
    };
  } catch {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    return null;
  }
}

function writePersisted(state: PersistedBookingState) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota exceeded — silently ignore */
  }
}

function clearPersisted() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function BookingFlow({ preselectedService, preselectedArea, bookingContext, enquiryDraft }: { preselectedService?: string; preselectedArea?: string; bookingContext?: string; enquiryDraft?: string }) {
  const biz = getBusinessConfig();
  const verification = useVerification();
  // Booking requires the admin-attested business details (address + postcode)
  // to be confirmed. When unverified we lock the form and route users to the
  // phone/WhatsApp fallbacks so no ambiguous booking is captured against a
  // stale NAP block. The address audit trail lives in the admin panel.
  const isVerified = verification.verified === true;
  const safePreselectedService = preselectedService && VALID_SERVICES.has(preselectedService) ? preselectedService : "";
  const safePreselectedArea = preselectedArea?.trim() || undefined;
  // bookingContext is sanitised by the route's zod schema (max 80 chars, [a-z0-9-]).
  // Append it as a non-validated note prefix so existing form validation is untouched.
  const safeContext = bookingContext && /^[a-z0-9-]{1,80}$/i.test(bookingContext) ? bookingContext : undefined;

  const initialNotes = [
    safeContext ? `Context: ${safeContext}` : null,
    safePreselectedArea ? `Visiting from ${safePreselectedArea}` : null,
    enquiryDraft?.trim() ? enquiryDraft.trim().slice(0, 500) : null,
  ].filter(Boolean).join(" — ");

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(safePreselectedService);
  const [otherDesc, setOtherDesc] = useState("");
  const [reg, setReg] = useState("");
  const [makeModel, setMakeModel] = useState("");
  const [year, setYear] = useState("");
  const [notes, setNotes] = useState(initialNotes);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [prefDate, setPrefDate] = useState("");
  const [prefTime, setPrefTime] = useState("Flexible");
  const [urgency, setUrgency] = useState("This Week");
  const [submitting, setSubmitting] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [error, setError] = useState("");
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const [storageReady, setStorageReady] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    reference: string;
    name: string;
    phone: string;
    serviceLabel: string;
  } | null>(null);

  useEffect(() => {
    const persisted = readPersisted();
    const validPersisted = persisted && (!safePreselectedArea || persisted.area === safePreselectedArea) ? persisted : null;
    if (!validPersisted) {
      setStorageReady(true);
      return;
    }
    setStep(validPersisted.step ?? 1);
    setSelectedService(safePreselectedService || validPersisted.selectedService || "");
    setOtherDesc(validPersisted.otherDesc || "");
    setReg(validPersisted.reg || "");
    setMakeModel(validPersisted.makeModel || "");
    setYear(validPersisted.year || "");
    setNotes(enquiryDraft?.trim() ? initialNotes : (validPersisted.notes ?? initialNotes));
    setName(validPersisted.name || "");
    setPhone(validPersisted.phone || "");
    setEmail(validPersisted.email || "");
    setPrefDate(validPersisted.prefDate || "");
    setPrefTime(timeSlots.includes(validPersisted.prefTime || "") ? validPersisted.prefTime! : "Flexible");
    setUrgency(urgencyOptions.includes(validPersisted.urgency || "") ? validPersisted.urgency! : "This Week");
    setStorageReady(true);
  }, [safePreselectedArea, safePreselectedService]);

  // Persist on every relevant change (skip the "done" step).
  useEffect(() => {
    if (!storageReady) return;
    if (step >= 4) return;
    writePersisted({
      step, selectedService, otherDesc, reg, makeModel, year, notes,
      name, phone, email, prefDate, prefTime, urgency,
      area: safePreselectedArea,
    });
  }, [storageReady, step, selectedService, otherDesc, reg, makeModel, year, notes, name, phone, email, prefDate, prefTime, urgency, safePreselectedArea]);

  // Field-level validation messages — friendly + actionable.
  const nameError = name.length > 0 && name.trim().length < 2 ? "Please enter your full name." : "";
  const phoneTrim = phone.replace(/[^\d+]/g, "");
  const phoneError =
    phone.length > 0 && phoneTrim.length < 7
      ? "Enter a UK phone number we can call you back on (at least 7 digits)."
      : "";
  const emailError =
    email.length > 0 && !/^\S+@\S+\.\S+$/.test(email.trim())
      ? "That email doesn't look right — leave blank if you'd rather we called."
      : "";
  const otherDescError =
    step === 1 && selectedService === "other" && otherDesc.trim().length < 3
      ? "Describe what you need so we can quote accurately."
      : "";

  const canProceed = () => {
    if (step === 1) return !!selectedService && !otherDescError;
    if (step === 2) return true;
    if (step === 3)
      return (
        name.trim().length >= 2 &&
        phoneTrim.length >= 7 &&
        !emailError
      );
    return true;
  };

  // Tappable-target helper — at least 44px height to meet WCAG 2.5.5.
  const tap = "min-h-[44px] active:scale-[0.99] motion-reduce:active:scale-100";
  const focus = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";



  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    trackEvent("booking_request_submit", { service: selectedService, ...(safePreselectedArea ? { area: safePreselectedArea } : {}) });

    try {
      const attribution = buildLeadAttribution({
        formLocation: "booking_flow",
        ctaLabel: "submit_booking",
        pageType: safePreselectedArea ? "area" : "booking",
      });
      const result = await submitLead({
        data: {
          type: "booking" as const,
          service: selectedService === "other" ? `Other: ${otherDesc}` : selectedService,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          vehicle_reg: reg.trim() || undefined,
          vehicle_make_model: makeModel.trim() || undefined,
          vehicle_year: year.trim() || undefined,
          notes: notes.trim() || undefined,
          urgency,
          preferred_date: prefDate || undefined,
          preferred_time: prefTime,
          area: safePreselectedArea || undefined,
          sms_opt_in: smsOptIn,
          ...attribution,
        },
      });
      const serviceLabel =
        selectedService === "other"
          ? (otherDesc.trim() || "Other")
          : (serviceChips.find((s) => s.value === selectedService)?.label ?? selectedService);
      const reference =
        (result && typeof (result as { reference?: unknown }).reference === "string"
          ? (result as { reference: string }).reference
          : null) ??
        // Fallback: derive a short local reference so the UI always shows one.
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase()
          : Math.random().toString(16).slice(2, 10).toUpperCase());
      setConfirmation({
        reference,
        name: name.trim(),
        phone: phone.trim(),
        serviceLabel,
      });
      clearPersisted();
      setStep(4);
    } catch (err) {
      setError("Something went wrong. Please try calling us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step === 1 && !hasTrackedStart) {
      trackEvent("booking_request_start");
      setHasTrackedStart(true);
    }
  }, [step, hasTrackedStart]);

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      {!isVerified && (
        <div
          data-testid="booking-verification-gate"
          role="alert"
          aria-live="polite"
          className="border-b border-amber-500/40 bg-amber-500/10 p-4 md:p-5"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <div className="space-y-2">
              <p className="text-sm font-bold text-foreground">
                Booking is temporarily paused
              </p>
              <p className="text-xs text-muted-foreground">
                We're confirming our verified business address and opening hours before accepting new online requests. In the meantime, please call or WhatsApp us — we'll book you in immediately.
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <a
                  href={biz.phoneHref}
                  onClick={() => trackEvent("call_click", { location: "booking_verification_gate" })}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90"
                  data-testid="booking-gate-call"
                >
                  <Phone className="h-3.5 w-3.5" /> Call {biz.phone}
                </a>
                <a
                  href={biz.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "booking_verification_gate" })}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#25D366] px-3 py-2 text-xs font-bold text-[#25D366] hover:bg-[#25D366]/10"
                  data-testid="booking-gate-whatsapp"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      <fieldset
        disabled={!isVerified}
        data-testid="booking-form-fieldset"
        aria-disabled={!isVerified}
        className={!isVerified ? "pointer-events-none opacity-60" : undefined}
      >
      {/* Progress bar */}
      <div className="flex border-b border-border">
        {["Service", "Vehicle", "Contact", "Done"].map((label, i) => (
          <div
            key={label}
            className={`flex-1 py-2.5 text-center text-[11px] font-bold uppercase tracking-wider transition-colors ${
              i + 1 === step
                ? "bg-primary text-primary-foreground"
                : i + 1 < step
                  ? "bg-primary/10 text-primary"
                  : "bg-secondary/50 text-muted-foreground"
            }`}
          >
            {i + 1 < step ? <Check className="h-3 w-3 inline mr-1" /> : null}
            {label}
          </div>
        ))}
      </div>

      <div className="p-5 md:p-6">
        {/* Step 1: Service */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold text-foreground">What do you need?</h3>
              <p className="text-sm text-muted-foreground mt-0.5">Select a service and we'll get back to you to confirm.</p>
            </div>
            <div role="radiogroup" aria-label="Choose a service" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {serviceChips.map((s) => {
                const active = selectedService === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelectedService(s.value)}
                    className={`${tap} ${focus} rounded-lg border px-3 py-3 text-sm font-medium transition-all text-left ${
                      active
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border bg-background text-foreground hover:border-primary/30"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            {selectedService === "other" && (
              <div className="space-y-1">
                <Label htmlFor="bf-other" className="text-xs">Describe what you need</Label>
                <Input
                  id="bf-other"
                  value={otherDesc}
                  onChange={(e) => setOtherDesc(e.target.value)}
                  placeholder="e.g. clutch replacement, exhaust repair…"
                  aria-invalid={!!otherDescError || undefined}
                  aria-describedby={otherDescError ? "bf-other-err" : undefined}
                />
                {otherDescError && (
                  <p id="bf-other-err" className="text-[11px] text-destructive font-medium">{otherDescError}</p>
                )}
              </div>
            )}

          </div>
        )}

        {/* Step 2: Vehicle */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold text-foreground">Vehicle details</h3>
              <p className="text-sm text-muted-foreground mt-0.5">Optional — helps us prepare before you arrive.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Registration</Label>
              <Input
                value={reg}
                onChange={(e) => setReg(e.target.value.toUpperCase())}
                placeholder="e.g. AB12 CDE"
                className="uppercase font-bold tracking-wider text-lg h-12 text-center bg-[#FFD700]/10 border-[#FFD700]/40 placeholder:text-muted-foreground placeholder:font-normal placeholder:tracking-normal placeholder:text-sm"
              />
              <p className="text-[11px] text-muted-foreground">We'll use this to pull up your vehicle details — saves time on the day.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Make / Model</Label>
                <Input value={makeModel} onChange={(e) => setMakeModel(e.target.value)} placeholder="e.g. Ford Focus" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Year (approx)</Label>
                <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2019" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Any notes about the issue</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Not sure? Just describe what's happening and we'll advise." />
            </div>
          </div>
        )}

        {/* Step 3: Contact */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold text-foreground">Your details</h3>
              <p className="text-sm text-muted-foreground mt-0.5">We'll call you back to confirm availability.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="bf-name" className="text-xs">Name *</Label>
                <Input
                  id="bf-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                  aria-invalid={!!nameError || undefined}
                  aria-describedby={nameError ? "bf-name-err" : undefined}
                />
                {nameError && <p id="bf-name-err" className="text-[11px] text-destructive font-medium">{nameError}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="bf-phone" className="text-xs">Phone *</Label>
                <Input
                  id="bf-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={`e.g. ${NAP.phone}`}
                  required
                  aria-invalid={!!phoneError || undefined}
                  aria-describedby={phoneError ? "bf-phone-err" : "bf-phone-hint"}
                />
                {phoneError ? (
                  <p id="bf-phone-err" className="text-[11px] text-destructive font-medium">{phoneError}</p>
                ) : (
                  <p id="bf-phone-hint" className="text-[11px] text-muted-foreground">We'll call you back — usually within a few hours.</p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bf-email" className="text-xs">Email (optional)</Label>
              <Input
                id="bf-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Your email"
                aria-invalid={!!emailError || undefined}
                aria-describedby={emailError ? "bf-email-err" : undefined}
              />
              {emailError && <p id="bf-email-err" className="text-[11px] text-destructive font-medium">{emailError}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Preferred date</Label>
                <Input type="date" value={prefDate} onChange={(e) => setPrefDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Preferred time</Label>
                <div role="radiogroup" aria-label="Preferred time" className="flex gap-1.5">
                  {timeSlots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={prefTime === t}
                      onClick={() => setPrefTime(t)}
                      className={`${tap} ${focus} flex-1 rounded-md border px-2 text-[12px] font-medium transition-all ${
                        prefTime === t ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"
                      }`}
                    >
                      {t.split(" ")[0]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Urgency</Label>
              <div role="radiogroup" aria-label="Urgency" className="flex gap-1.5">
                {urgencyOptions.map((u) => (
                  <button
                    key={u}
                    type="button"
                    role="radio"
                    aria-checked={urgency === u}
                    onClick={() => setUrgency(u)}
                    className={`${tap} ${focus} flex-1 rounded-md border px-2 text-xs font-medium transition-all ${
                      urgency === u ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
              />
              <span className="text-xs text-foreground leading-snug">
                <span className="font-medium">Text me a confirmation</span> with the address, entrance directions and next steps. Reply STOP to opt out.
              </span>
            </label>
            <div aria-live="polite" className="min-h-[20px]">
              {error && (
                <p className="text-sm text-destructive font-medium">
                  {error} You can also call us on{" "}
                  <a href={biz.phoneHref} className="underline font-bold">{biz.phone}</a>.
                </p>
              )}
            </div>
          </div>
        )}


        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div
            className="text-center py-6 space-y-4"
            data-testid="booking-confirmation"
            role="status"
            aria-live="polite"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Request Received</h3>
              <p className="mt-1 text-sm text-muted-foreground">We'll call you back to confirm availability and timing.</p>
              <p className="mt-1 text-xs text-muted-foreground">We typically respond within a few hours during working hours (Mon–Sat, 9am–6pm).</p>
            </div>
            {confirmation && (
              <div className="mx-auto max-w-sm rounded-lg border border-border bg-muted/40 p-3 text-left text-sm">
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your reference</span>
                  <span
                    data-testid="booking-confirmation-reference"
                    className="font-mono text-sm font-bold text-primary"
                  >
                    {confirmation.reference}
                  </span>
                </div>
                <dl className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Name</dt>
                    <dd data-testid="booking-confirmation-name" className="font-medium text-foreground text-right truncate">{confirmation.name}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Phone</dt>
                    <dd data-testid="booking-confirmation-phone" className="font-medium text-foreground text-right">{confirmation.phone}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Service</dt>
                    <dd data-testid="booking-confirmation-service" className="font-medium text-foreground text-right">{confirmation.serviceLabel}</dd>
                  </div>
                </dl>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Please quote <span className="font-mono font-bold text-foreground">{confirmation.reference}</span> when we call you back.
                </p>
              </div>
            )}
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: "booking_confirmation" })}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-all"
            >
              <Phone className="h-5 w-5" />
              Call {biz.phone}
            </a>
            <div>
              <a
                href={getWhatsAppUrl(selectedService)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "booking_confirmation" })}
                className="inline-flex items-center gap-2 text-sm text-[#25D366] font-semibold hover:text-[#25D366]/80 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Prefer to WhatsApp? Tap here
              </a>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} className="gap-1">
                <ArrowLeft className="h-3 w-3" /> Back
              </Button>
            ) : (
              <a href={biz.phoneHref} onClick={() => trackEvent("call_click", { location: "booking_flow" })} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                <Phone className="h-3 w-3 inline mr-1" />Prefer to call?
              </a>
            )}
            <Button
              onClick={() => (step === 3 ? handleSubmit() : setStep(step + 1))}
              disabled={!canProceed() || submitting}
              className="gap-1"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Sending…
                </>
              ) : step === 3 ? (
                "Send Request"
              ) : (
                <>
                  Continue <ArrowRight className="h-3 w-3" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      </fieldset>
    </div>
  );
}
